import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, Settings, User, CreditCard, LogOut } from 'lucide-react';
import API_URL from '../config/API_URL';

export const Header = ({ userName, userType }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const color = userType === 'grandparent' ? '#9333ea' : '#db2777';
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleLogoClick = () => {
    // Navigate to appropriate dashboard based on user type
    if (userType === 'parent') {
      navigate('/parent-dashboard');
    } else if (userType === 'grandparent') {
      navigate('/grandparent-dashboard');
    }
  };

  const handleSettingsClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setShowDropdown(false);
  };

  const handleLogout = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Redirect to login page after successful logout
        navigate('/login');
      } else {
        console.error('Logout failed');
        // Still redirect even if there's an error
        navigate('/login');
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Still redirect even if there's an error
      navigate('/login');
    }
    setShowDropdown(false);
  };

  return (
    <div style={{ background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderBottom: '1px solid #e5e7eb' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Logo/Brand with click handler */}
        <div 
          onClick={handleLogoClick}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            cursor: 'pointer',
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <Book style={{ width: '2rem', height: '2rem', color: '#ec4899' }} />
<span style={{ 
  fontSize: '1.5rem',
  fontWeight: '900',
  fontFamily: '"Comic Sans MS", "Chalkboard SE", "Comic Neue", cursive, sans-serif',
  display: 'flex', 
  gap: '0.1rem',
  letterSpacing: '0.05rem'
}}>
  <span style={{ 
    color: '#ff4757',
    WebkitTextStroke: '3px black',
    paintOrder: 'stroke fill',
    textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
  }}>R</span>
  <span style={{ 
    color: '#ff6b35',
    WebkitTextStroke: '3px black',
    paintOrder: 'stroke fill',
    textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
  }}>e</span>
  <span style={{ 
    color: '#ffd93d',
    WebkitTextStroke: '3px black',
    paintOrder: 'stroke fill',
    textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
  }}>a</span>
  <span style={{ 
    color: '#a855f7',
    WebkitTextStroke: '3px black',
    paintOrder: 'stroke fill',
    textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
  }}>d</span>
  <span style={{ 
    color: '#ffd93d',
    WebkitTextStroke: '3px black',
    paintOrder: 'stroke fill',
    textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
  }}>2</span>
  <span style={{ 
    color: '#06b6d4',
    WebkitTextStroke: '3px black',
    paintOrder: 'stroke fill',
    textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
  }}>M</span>
  <span style={{ 
    color: '#3b82f6',
    WebkitTextStroke: '3px black',
    paintOrder: 'stroke fill',
    textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
  }}>e</span>
</span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ color: '#374151', fontWeight: '500' }}>Hi, {userName}! 👋</span>
          
          {/* Settings button with dropdown */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button 
              onClick={handleSettingsClick}
              style={{ 
                padding: '0.5rem', 
                background: showDropdown ? '#f3f4f6' : 'transparent', 
                border: 'none', 
                borderRadius: '0.5rem', 
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
            >
              <Settings style={{ width: '1.25rem', height: '1.25rem', color: '#4b5563' }} />
            </button>

            {/* Dropdown menu for parent users */}
            {showDropdown && userType === 'parent' && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 0.5rem)',
                right: 0,
                background: 'white',
                borderRadius: '0.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                border: '1px solid #e5e7eb',
                minWidth: '200px',
                zIndex: 50,
                overflow: 'hidden'
              }}>
                <div style={{ padding: '0.5rem 0' }}>
                  <button
                    onClick={() => handleNavigate('/profile')}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      transition: 'background 0.2s',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <User size={18} style={{ color: '#6b7280' }} />
                    <span>Manage Profile</span>
                  </button>

                  <button
                    onClick={() => handleNavigate('/subscription')}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      transition: 'background 0.2s',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <CreditCard size={18} style={{ color: '#6b7280' }} />
                    <span>Manage Subscription</span>
                  </button>

                  {/* Divider */}
                  <div style={{ height: '1px', background: '#e5e7eb', margin: '0.5rem 0' }}></div>

                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#dc2626',
                      transition: 'background 0.2s',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut size={18} style={{ color: '#dc2626' }} />
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            )}

            {/* Dropdown menu for grandparent users */}
            {showDropdown && userType === 'grandparent' && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 0.5rem)',
                right: 0,
                background: 'white',
                borderRadius: '0.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                border: '1px solid #e5e7eb',
                minWidth: '200px',
                zIndex: 50,
                overflow: 'hidden'
              }}>
                <div style={{ padding: '0.5rem 0' }}>
                  <button
                    onClick={() => handleNavigate('/profile')}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      transition: 'background 0.2s',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <User size={18} style={{ color: '#6b7280' }} />
                    <span>Manage Profile</span>
                  </button>
                  <button
                    onClick={() => handleNavigate('/subscription')}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      transition: 'background 0.2s',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <CreditCard size={18} style={{ color: '#6b7280' }} />
                    <span>Manage Subscription</span>
                  </button>

                  {/* Divider */}
                  <div style={{ height: '1px', background: '#e5e7eb', margin: '0.5rem 0' }}></div>

                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#dc2626',
                      transition: 'background 0.2s',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut size={18} style={{ color: '#dc2626' }} />
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};